/**
 * Content-depth scope, set from Semrush US volume/KD (August 2026 pull).
 *
 * Only pages listed here get the extended, blunt-voice analysis blocks. Every
 * other attribute page keeps its existing data table and standard narrative:
 * no new URLs, no sitemap growth, no reindexing changes.
 *
 * Key format: `${makeSlug}/${modelSlug}`.
 */

export type DepthTemplate =
  | "towing-capacity"
  | "bolt-pattern"
  | "tire-size"
  | "mpg"
  | "bed-dimensions"
  | "fuel-tank-capacity"
  | "dimensions"
  | "safety-ratings";

/** Tier 1 — highest volume, lowest KD template on the site (3–16 KD). */
const TOWING = [
  "toyota/tacoma",
  "toyota/highlander",
  "toyota/4runner",
  "toyota/tundra",
  "toyota/rav4",
  "toyota/sequoia",
  "ford/f150",
  "ford/f-150",
  "ford/ranger",
  "ford/explorer",
  "ford/expedition",
  "ford/bronco",
  "ford/f250",
  "ford/f350",
  "ram/1500",
  "ram/2500",
  "jeep/grand-cherokee",
  "jeep/wrangler",
  "jeep/gladiator",
  "honda/pilot",
  "honda/ridgeline",
  "honda/odyssey",
  "subaru/outback",
  "subaru/ascent",
  "chevrolet/silverado",
  "chevrolet/tahoe",
  "chevrolet/colorado",
  "gmc/sierra",
  "nissan/frontier",
  "toyota/land-cruiser",
];

/** Tier 2 — lowest competition on the site (0.01–0.31). */
const BOLT_PATTERN = [
  "jeep/wrangler",
  "ford/f150",
  "ford/f-150",
  "ram/1500",
  "chevrolet/silverado",
  "toyota/tacoma",
  "honda/civic",
  "toyota/camry",
  "toyota/4runner",
  "jeep/grand-cherokee",
  "ford/mustang",
  "subaru/wrx",
  "honda/accord",
  "nissan/altima",
  "chevrolet/camaro",
];

const TIRE_SIZE = [
  "toyota/tacoma",
  "ford/f150",
  "ford/f-150",
  "toyota/rav4",
  "honda/crv",
  "honda/cr-v",
  "jeep/wrangler",
  "toyota/4runner",
  "chevrolet/silverado",
  "honda/civic",
];

/** Tier 2 — real demand, concentrated in a handful of nameplates. */
const MPG = [
  "toyota/tacoma",
  "toyota/4runner",
  "toyota/rav4",
  "toyota/tundra",
  "toyota/camry",
  "jeep/wrangler",
  "ford/f150",
  "ford/f-150",
  "ford/bronco",
  "ford/escape",
  "subaru/outback",
  "subaru/forester",
  "honda/accord",
  "honda/crv",
  "honda/cr-v",
  "chevrolet/tahoe",
];

const BED_DIMENSIONS = [
  "toyota/tacoma",
  "toyota/tundra",
  "ford/f150",
  "ford/f-150",
  "ford/ranger",
  "ford/f250",
  "ford/f350",
  "ram/1500",
  "chevrolet/silverado",
  "chevrolet/colorado",
  "gmc/sierra",
  "nissan/frontier",
];

const FUEL_TANK = [
  "toyota/rav4",
  "toyota/tacoma",
  "honda/crv",
  "honda/cr-v",
  "ford/f150",
  "ford/f-150",
  "toyota/camry",
  "honda/civic",
  "toyota/4runner",
  "jeep/wrangler",
  "chevrolet/silverado",
  "subaru/outback",
];

/** Tier 3 — only the low-KD outliers; the rest is Edmunds/OEM territory. */
const DIMENSIONS = [
  "ford/f150",
  "ford/f-150",
  "toyota/rav4",
  "chevrolet/tahoe",
  "ford/escape",
];

/** Tier 4 — the only model-level safety pages clearing ~300/mo. */
const SAFETY = [
  "nissan/rogue",
  "toyota/rav4",
  "tesla/model-y",
  "ford/explorer",
  "jeep/grand-cherokee",
  "subaru/outback",
  "honda/pilot",
  "honda/crv",
  "honda/cr-v",
];

const SCOPE: Record<DepthTemplate, Set<string>> = {
  "towing-capacity": new Set(TOWING),
  "bolt-pattern": new Set(BOLT_PATTERN),
  "tire-size": new Set(TIRE_SIZE),
  mpg: new Set(MPG),
  "bed-dimensions": new Set(BED_DIMENSIONS),
  "fuel-tank-capacity": new Set(FUEL_TANK),
  dimensions: new Set(DIMENSIONS),
  "safety-ratings": new Set(SAFETY),
};

/**
 * Tier 5 — brand-level maintenance-schedule pages. Brand queries ("toyota
 * maintenance schedule") carry the volume here; model-level ones do not.
 */
const MAINTENANCE_BRANDS = new Set([
  "toyota",
  "honda",
  "ford",
  "chevrolet",
  "bmw",
  "mercedes-benz",
  "subaru",
  "nissan",
  "jeep",
  "audi",
]);

export function isMaintenanceDepthBrand(makeSlug: string): boolean {
  return MAINTENANCE_BRANDS.has(makeSlug.toLowerCase());
}

export function isDepthScoped(
  dataType: string,
  makeSlug: string,
  modelSlug: string,
): dataType is DepthTemplate {
  const set = SCOPE[dataType as DepthTemplate];
  if (!set) return false;
  return set.has(`${makeSlug.toLowerCase()}/${modelSlug.toLowerCase()}`);
}

/** Total pages currently in scope — used by audit scripts. */
export function depthScopeSize(): number {
  return Object.values(SCOPE).reduce((n, s) => n + s.size, 0);
}
